import React from 'react';
import { Divider, Box, Accordion, AccordionSummary, AccordionDetails, Chip } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Link } from 'react-router-dom';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { a11yDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { Stack } from 'new/elements/Stack';
import Typography from 'new/elements/TypographySupport/TypographySupport';
import CodeContainer from 'js/components/data_sources/add_ds_form/IncomingWebhookConfiguration/CodeContainer';
import { sanitizeApiKey } from 'js/lib/URLUtils';
import { ROUTE_SETTINGS_AUTHENTICATION } from 'new/constants/pathnames';
import useEntities from 'new/state/entities/repository/useEntities';
import { DataSource } from 'new/entities/DataSource';
import ApiServerParameterTable from 'js/components/data_sources/cards/configViews/ApiServerParameterTable';
import {
  generateCurlExample,
  hasEndpointConfiguration,
  getHttpMethodColor,
} from 'js/lib/ApiServerUtils';
import CopyButton from '../../add_ds_form/IncomingWebhookConfiguration/CopyButton';

type EndpointKeyValue = {
  key: string;
  value: string;
  required: boolean;
};

interface EndpointSpec {
  id: number;
  org_id: number;
  owner_id: number;
  data_set_id: number;
  method: string;
  route: string;
  headers?: Array<EndpointKeyValue>;
  path_params?: Array<EndpointKeyValue>;
  query_params?: Array<EndpointKeyValue>;
  body?: any;
  created_at: string;
  updated_at: string;
}

interface ApiServerConfigViewProps {
  data: DataSource;
}

export default function ApiServerConfigView(props: ApiServerConfigViewProps) {
  const { data } = props;
  const { endpoint_mappings, api_keys } = data;
  const { data: apiKeys } = useEntities('apiKeys');
  const { data: dataSets } = useEntities('dataSets');

  // Create a map of data_set_id to endpoint_spec for quick lookup
  const endpointSpecMap = React.useMemo(() => {
    const map: { [key: number]: any } = {};
    endpoint_mappings?.forEach((mapping) => {
      const dataset = dataSets[mapping.data_set_id];
      if (dataset?.endpoint_spec) {
        map[mapping.data_set_id] = dataset.endpoint_spec;
      }
    });
    return map;
  }, [endpoint_mappings, dataSets]);

  const primaryApiKeyId = api_keys?.[0];
  const primaryApiKey = primaryApiKeyId ? apiKeys[primaryApiKeyId] : null;
  const sanitizedApiKey = primaryApiKey ? sanitizeApiKey(primaryApiKey.api_key) : '';

  return (
    <Stack paddingBottom="24px" gap="16px">
      <Typography variant="bodyDefault">
        This API Server provides RESTful endpoints for your data flows, allowing external
        applications to consume your processed data through HTTP requests. The API Server
        automatically handles authentication, request validation, and response formatting. Use the
        configuration details below to integrate with your applications and monitor usage.
      </Typography>
      <Divider />
      <Typography variant="subtitle2">API Access</Typography>
      <Typography variant="bodyDefault">
        Use the following API key and endpoint URL to authenticate and access your API Server. The
        API key should be included as a query parameter in your requests. For security purposes, you
        can rotate your API keys at any time by visiting the{' '}
        <Link to={ROUTE_SETTINGS_AUTHENTICATION} target="_blank">
          Settings {'\u2192'} Authentication
        </Link>{' '}
        page.
      </Typography>
      {primaryApiKey ? (
        <>
          <CodeContainer
            toCopy={primaryApiKey.url}
            value={primaryApiKey.url}
            label="API Server URL"
          />
          <CodeContainer
            toCopy={primaryApiKey.api_key}
            value={`api_key=${sanitizedApiKey}`}
            label="API Key (Query Parameter)"
          />
        </>
      ) : (
        <Typography variant="body2">No API keys configured</Typography>
      )}
      <Divider />
      <Typography variant="subtitle2">API Endpoints</Typography>
      {endpoint_mappings.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {endpoint_mappings.map((mapping) => {
            const endpointSpec: EndpointSpec | undefined = endpointSpecMap[mapping.data_set_id];
            const methodChipColor = getHttpMethodColor(mapping.method) as any;
            return (
              <Accordion
                key={mapping.id}
                sx={{
                  '&:before': { display: 'none' },
                  boxShadow: 'none',
                  borderRadius: '8px !important',
                  '&:first-of-type': {
                    borderRadius: '8px !important',
                  },
                  '&:last-of-type': {
                    borderRadius: '8px !important',
                  },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    '& .MuiAccordionSummary-content': {
                      alignItems: 'center',
                      gap: 1,
                    },
                    '& .MuiAccordionSummary-expandIconWrapper': {
                      color: 'var(--color-greyscale-800)',
                    },
                  }}
                >
                  <Chip label={mapping.method} color={methodChipColor} variant="outlined" />
                  <Typography variant="body2">{mapping.route || 'No route configured'}</Typography>
                  {mapping.description && (
                    <Typography variant="body2" sx={{ ml: 'auto' }}>
                      {mapping.description}
                    </Typography>
                  )}
                </AccordionSummary>
                <AccordionDetails>
                  <Stack gap="8px" direction="column">
                    {mapping.description && (
                      <Typography variant="body2">{mapping.description}</Typography>
                    )}
                    <Stack gap="16px" direction="row" sx={{ alignItems: 'stretch' }}>
                      {hasEndpointConfiguration(endpointSpec) ? (
                        <Stack gap="24px" direction="column" sx={{ flex: 1, minWidth: 0 }}>
                          <ApiServerParameterTable
                            params={endpointSpec.headers || []}
                            title="Headers"
                          />
                          <ApiServerParameterTable
                            params={endpointSpec.path_params || []}
                            title="Path Parameters"
                          />
                          <ApiServerParameterTable
                            params={endpointSpec.query_params || []}
                            title="Query Parameters"
                          />
                          <ApiServerParameterTable
                            body={endpointSpec.body}
                            title="Request Body"
                            subtitle="application/json"
                          />
                        </Stack>
                      ) : (
                        <Box
                          sx={{
                            flex: 1,
                            minWidth: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <Typography variant="body2">
                            No configured endpoint specification
                          </Typography>
                        </Box>
                      )}
                      {primaryApiKey && endpointSpec && (
                        <Box sx={{ flex: 1, minWidth: 0, maxWidth: '500px' }}>
                          <Stack
                            justifyContent="space-between"
                            direction="row"
                            alignItems="center"
                            marginBottom="8px"
                          >
                            <Typography variant="subtitle2">cURL Example</Typography>
                            <CopyButton
                              title="Copy cURL"
                              toCopy={generateCurlExample(
                                endpointSpec,
                                primaryApiKey.url,
                                primaryApiKey.api_key,
                                false,
                              )}
                            />
                          </Stack>
                          <Box
                            sx={{
                              '& pre': {
                                whiteSpace: 'pre-wrap !important',
                                wordBreak: 'break-word !important',
                                overflowWrap: 'break-word !important',
                              },
                              '& code': {
                                whiteSpace: 'pre-wrap !important',
                                wordBreak: 'break-word !important',
                              },
                            }}
                          >
                            <SyntaxHighlighter
                              language="bash"
                              style={a11yDark}
                              customStyle={{
                                borderRadius: '4px',
                                fontSize: '0.75rem',
                                margin: 0,
                                maxWidth: '100%',
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                              }}
                              wrapLongLines={true}
                            >
                              {generateCurlExample(
                                endpointSpec,
                                primaryApiKey.url,
                                primaryApiKey.api_key,
                                true,
                              )}
                            </SyntaxHighlighter>
                          </Box>
                        </Box>
                      )}
                    </Stack>
                  </Stack>
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Box>
      )}
    </Stack>
  );
}
